import WarpElement from "@warp-ds/elements-core";
import { css, html } from "lit";
import { TrackingEventMap, eventReference } from "../../models/trackingEvent.js";
import { trackClickEvent } from "../tracking.js";
import type { WebTabKey as TabKey } from "../../shared/tab-deeplink.js";

const TAB_EVENT_NAME: Record<TabKey, string> = {
    all: "All Cars Tab",
    newCar: "New Car Tab",
    sell: "Sell Tab",
};

class LandingTabs extends WarpElement {
    declare activeTab: TabKey;
    declare allLabel: string;
    declare sellLabel: string;
    // Empty unless the New Car tab is present; its presence drives whether the third tab renders.
    declare newCarLabel: string;
    declare trackingEventMap: TrackingEventMap;

    static properties = {
        activeTab: { type: String, attribute: "active-tab", reflect: true },
        allLabel: { type: String, attribute: "all-label" },
        sellLabel: { type: String, attribute: "sell-label" },
        newCarLabel: { type: String, attribute: "new-car-label" },
        trackingEventMap: { type: Object },
    };

    constructor() {
        super();
        this.activeTab = "all";
        this.allLabel = "All";
        this.sellLabel = "Sell";
        this.newCarLabel = "";
    }

    static styles = [
        WarpElement.styles,
        css`
            :host {
                display: block;
            }
            @css-placeholder-JrNglw;
            [role="tablist"] {
                position: sticky;
                top: 48px;
                z-index: 10;
                background-color: var(--w-s-color-background, #fff);
            }
            [role="tablist"]::before {
                content: "";
                position: absolute;
                top: 0;
                left: 50%;
                transform: translateX(-50%);
                width: 100vw;
                height: 100%;
                background-color: var(--w-s-color-background, #fff);
                z-index: -1;
            }
            @media (min-width: 768px) {
                [role="tab"] {
                    flex: 0 0 auto;
                    padding: 8px 12px;
                    text-align: left;
                }
            }
        `,
    ];

    #setTab(tab: TabKey) {
        this.activeTab = tab;
        this.#syncUrl(tab);
        const baseEvent = this.trackingEventMap?.[eventReference.TAB_CLICK];
        if (baseEvent) {
            trackClickEvent({ ...baseEvent, name: TAB_EVENT_NAME[tab] });
        }
    }

    /**
     * Keep the URL in sync with the active tab so it is shareable/bookmarkable and
     * consistent with the `?tab=` deep-link param (MPB-3525). The default tab ("all")
     * drops the param to keep canonical URLs clean. Uses replaceState so switching tabs
     * does not pollute the browser history.
     */
    #syncUrl(tab: TabKey) {
        if (typeof window === "undefined" || !window.history?.replaceState) return;
        const url = new URL(window.location.href);
        if (tab === "all") {
            url.searchParams.delete("tab");
        } else {
            // Lowercase to match the documented `?tab=` vocabulary (e.g. `newcar`, not `newCar`).
            url.searchParams.set("tab", tab.toLowerCase());
        }
        window.history.replaceState(window.history.state, "", url);
    }

    // Single source of truth for the visible tabs, in render order: drives both the rendered
    // tablist/panels and keyboard navigation so the two cannot desync. `key` is the TabKey,
    // `id` the DOM-safe slot/id suffix (kebab-case). The New Car tab is present only when labelled.
    #orderedTabs(): { key: TabKey; id: string; label: string }[] {
        const tabs = [{ key: "all", id: "all", label: this.allLabel }] as { key: TabKey; id: string; label: string }[];
        if (this.newCarLabel) {
            tabs.push({ key: "newCar", id: "new-car", label: this.newCarLabel });
        }
        tabs.push({ key: "sell", id: "sell", label: this.sellLabel });
        return tabs;
    }

    // WAI-ARIA Tabs keyboard pattern (horizontal tablist): Left/Right move between tabs with
    // wraparound, Home/End jump to the ends. Automatic activation — moving focus selects
    // the tab — matching the click behaviour. Roving tabindex keeps a single tab stop.
    #onTablistKeydown(event: KeyboardEvent) {
        const tabs = this.#orderedTabs();
        const currentIndex = tabs.findIndex((tab) => tab.key === this.activeTab);
        // Bail if the active tab is not in the visible set (e.g. an unexpected active-tab value);
        // otherwise findIndex === -1 would make arrow keys jump to a misleading position.
        if (currentIndex < 0) return;
        let nextIndex: number;
        switch (event.key) {
            case "ArrowRight":
                nextIndex = (currentIndex + 1) % tabs.length;
                break;
            case "ArrowLeft":
                nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
                break;
            case "Home":
                nextIndex = 0;
                break;
            case "End":
                nextIndex = tabs.length - 1;
                break;
            default:
                return;
        }
        event.preventDefault();
        const next = tabs[nextIndex];
        this.#setTab(next.key);
        // Move focus to the newly selected tab once the roving tabindex has been re-rendered.
        void this.updateComplete.then(() => {
            this.renderRoot.querySelector<HTMLButtonElement>(`#landing-tab-${next.id}`)?.focus();
        });
    }

    render() {
        const tabStyle = (active: boolean) =>
            `border: 0; border-bottom: 2px solid ${active ? "var(--w-s-color-link, #0063fb)" : "transparent"};`;

        const orderedTabs = this.#orderedTabs();
        // Roving tabindex: the active tab is the single tab stop; arrows move between the rest.
        const renderTab = ({ key, id, label }: { key: TabKey; id: string; label: string }) => {
            const active = this.activeTab === key;
            return html`<button
                type="button"
                role="tab"
                id="landing-tab-${id}"
                aria-selected="${active ? "true" : "false"}"
                aria-controls="landing-panel-${id}"
                tabindex="${active ? "0" : "-1"}"
                class="flex-1 px-16 py-12 text-center text-body font-bold bg-transparent cursor-pointer ${
                    active ? "s-text-link" : "s-text-subtle hover:s-text"
                }"
                style="${tabStyle(active)}"
                @click="${() => this.#setTab(key)}"
            >
                ${label}
            </button>`;
        };
        // Every panel carries focusable content (links, incl. the New Car cards), so it's reached
        // via that content and needs no tabindex of its own.
        const renderPanel = ({ key, id }: { key: TabKey; id: string }) =>
            html`<div id="landing-panel-${id}" role="tabpanel" aria-labelledby="landing-tab-${id}" ?hidden="${this.activeTab !== key}">
                <slot name="${id}"></slot>
            </div>`;

        // Map both from the same ordered list so the tablist, panels, and keyboard nav never desync.
        const tabs = orderedTabs.map(renderTab);
        const panels = orderedTabs.map(renderPanel);

        return html`
            <div
                class="flex gap-24 border-b s-border mb-16"
                role="tablist"
                aria-label="Landing page sections"
                @keydown="${(event: KeyboardEvent) => this.#onTablistKeydown(event)}"
            >
                ${tabs}
            </div>
            ${panels}
        `;
    }
}

customElements.define("landing-tabs", LandingTabs as unknown as CustomElementConstructor);
